import { useEffect, useMemo, useState } from 'react'

import type {
  UsersAccessTechnicianFormErrors,
  UsersAccessTechnicianFormValuesI,
} from '../types/usersAccess'
import { useUsersAccessFacilityOptions } from './useUsersAccessFacilityOptions'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import * as EmailValidator from 'email-validator'

import type { ColorScheme, MultiSelectOption } from '@hivewatch/dark'

import {
  TechniciansDocument,
  useTechnicianQuery,
  useUpdateTechnicianMutation,
} from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

const EMPTY_VALUES: UsersAccessTechnicianFormValuesI = {
  deviceTypes: [],
  email: '',
  facilityIds: [],
  firstName: '',
  lastName: '',
}

export const useUsersAccessTechnicianEditor = (
  technicianId: string | null,
  colorScheme: ColorScheme
) => {
  const { t } = useTranslation()
  const { shouldEnableUpdateTechnician } = usePermissions()
  const { showError, showSuccess } = useToasts({
    colorScheme,
    isRebrand: true,
  })
  const [values, setValues] =
    useState<UsersAccessTechnicianFormValuesI>(EMPTY_VALUES)
  const [errors, setErrors] = useState<UsersAccessTechnicianFormErrors>({})
  const [isSeeded, setIsSeeded] = useState(false)

  const {
    data: technicianData,
    loading: isTechnicianLoading,
    error: technicianError,
  } = useTechnicianQuery({
    fetchPolicy: 'network-only',
    skip: !technicianId,
    variables: { id: technicianId ?? '' },
  })
  const {
    isFacilitiesError,
    facilityOptions,
    isFacilitiesLoading,
    setFacilitySearch,
  } = useUsersAccessFacilityOptions()
  const [updateTechnician, { loading: isSaving }] =
    useUpdateTechnicianMutation()

  useEffect(() => {
    setValues(EMPTY_VALUES)
    setErrors({})
    setIsSeeded(false)
  }, [technicianId])

  useEffect(() => {
    const technician = technicianData?.technician
    if (!technician || isSeeded) return

    setValues({
      deviceTypes: (technician.deviceTypes ?? []).filter(
        (type): type is DeviceType => type !== null
      ),
      email: technician.email ?? '',
      facilityIds:
        technician.facilities?.flatMap((facility) =>
          facility?.id ? [facility.id] : []
        ) ?? [],
      firstName: technician.firstName ?? '',
      lastName: technician.lastName ?? '',
    })
    setIsSeeded(true)
  }, [technicianData, isSeeded])

  useEffect(() => {
    if (!technicianError) return
    Sentry.captureException(technicianError, {
      tags: {
        action: 'load_technician_editor',
        feature: 'managed_accounts',
        surface: 'settings_users_access',
      },
    })
  }, [technicianError])

  useEffect(() => {
    if (!isFacilitiesError) return
    // useFacilitySearch only surfaces a boolean — the underlying error is
    // already captured there with generic search tags; this adds the
    // surface-specific context worth triaging on separately.
    Sentry.captureMessage('Failed to load facilities for technician editor', {
      level: 'error',
      tags: {
        action: 'load_technician_editor',
        feature: 'managed_accounts',
        surface: 'settings_users_access',
      },
    })
  }, [isFacilitiesError])

  const selectedFacilityOptions: MultiSelectOption[] = useMemo(
    () =>
      technicianData?.technician?.facilities
        ?.flatMap((facility) => (facility ? [facility] : []))
        .map((facility) => ({
          value: facility.id,
          label: facility.name ?? facility.shortName ?? '',
        }))
        .filter((option) => Boolean(option.label)) ?? [],
    [technicianData]
  )

  const setField = <FieldT extends keyof UsersAccessTechnicianFormValuesI>(
    field: FieldT,
    value: UsersAccessTechnicianFormValuesI[FieldT]
  ) => {
    setValues((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
  }

  const validate = () => {
    const nextErrors: UsersAccessTechnicianFormErrors = {}
    if (!values.firstName.trim()) {
      nextErrors.firstName = t('settings:rebrand_users_first_name_required')
    }
    if (!values.lastName.trim()) {
      nextErrors.lastName = t('settings:rebrand_users_last_name_required')
    }
    if (!EmailValidator.validate(values.email.trim())) {
      nextErrors.email = t('settings:rebrand_users_email_invalid')
    }
    if (values.facilityIds.length === 0) {
      nextErrors.facilityIds = t('settings:rebrand_users_facilities_required')
    }
    if (values.deviceTypes.length === 0) {
      nextErrors.deviceTypes = t('settings:rebrand_users_device_types_required')
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const submit = async () => {
    if (!technicianId || !shouldEnableUpdateTechnician || !validate()) {
      return false
    }
    try {
      await updateTechnician({
        variables: {
          input: {
            id: technicianId,
            deviceTypes: values.deviceTypes,
            email: values.email.trim(),
            facilityIds: values.facilityIds,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
          },
        },
        refetchQueries: [{ query: TechniciansDocument }],
      })
      showSuccess(t('settings:rebrand_users_edit_technician_success'))
      return true
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'edit_technician',
          feature: 'managed_accounts',
          surface: 'settings_users_access',
        },
      })
      showError(t('settings:rebrand_users_action_error'))
      return false
    }
  }

  return {
    canEdit: shouldEnableUpdateTechnician,
    errors,
    facilityOptions,
    isError: Boolean(technicianError) || isFacilitiesError,
    isLoading: isTechnicianLoading || isFacilitiesLoading,
    isSaving,
    selectedFacilityOptions,
    setFacilitySearch,
    setField,
    submit,
    values,
  }
}
